import AdminProductManager from './AdminProductManager';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

interface ModernAdminDashboardProps {
  t: any;
  isRTL: boolean;
  onLogout: () => void;
}

interface ContentItem {
  id: string;
  updatedAt?: any;
  createdAt?: any;
  [key: string]: any;
}

const getMenuItems = (t: any) => [
  {
    id: 'overview',
    title: t.admin.overview,
    icon: '/images/admin/dashboard.svg',
    section: 'main'
  },
  {
    id: 'home',
    title: t.home,
    icon: '/images/admin/home.svg',
    section: 'main'
  },
  {
    id: 'about',
    title: t.about,
    icon: '/images/admin/about.svg',
    section: 'main'
  },
  {
    id: 'services',
    title: t.services,
    icon: '/images/admin/services.svg',
    section: 'main'
  },
  {
    id: 'products',
    title: t.products,
    icon: '/images/admin/products.svg',
    section: 'main'
  },
  {
    id: 'markets',
    title: t.markets,
    icon: '/images/admin/markets.svg',
    section: 'main'
  },
  {
    id: 'partners',
    title: t.partners,
    icon: '/images/admin/partners.svg',
    section: 'main'
  },
  {
    id: 'contact',
    title: t.contact,
    icon: '/images/admin/settings.svg',
    section: 'main'
  }
];

const getStats = (t: any) => [
  {
    title: t.admin.totalProducts,
    value: '150+',
    change: '+12%',
    positive: true,
    icon: '/images/admin/products.svg',
    color: '#dc2626'
  },
  {
    title: t.admin.activeMarkets,
    value: '25',
    change: '+5%',
    positive: true,
    icon: '/images/admin/markets.svg',
    color: '#10b981'
  },
  {
    title: t.admin.partners,
    value: '50+',
    change: '+8%',
    positive: true,
    icon: '/images/admin/partners.svg',
    color: '#f59e0b'
  },
  {
    title: t.admin.globalReach,
    value: '15',
    change: '+3%',
    positive: true,
    icon: '/images/admin/services.svg',
    color: '#8b5cf6'
  }
];

const DashboardContainer = styled.div<{ $isRTL: boolean }>`
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
  overflow-x: hidden;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Sidebar = styled(motion.aside)<{ $collapsed: boolean; $isRTL: boolean }>`
  width: ${props => props.$collapsed ? '80px' : '280px'};
  background: linear-gradient(180deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1000;
  ${props => props.$isRTL && `
    order: 2;
  `}
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 80px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  img {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    filter: brightness(0) invert(1); /* Makes the logo white */
  }
  
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
  color: white;
  margin: 0;
  }
`;

const MenuSection = styled.div`
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Hide horizontal scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const MenuSectionTitle = styled.h3<{ $collapsed: boolean }>`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 1.5rem 1rem;
  opacity: ${props => props.$collapsed ? 0 : 1};
  transition: opacity 0.3s ease;
`;

const MenuItem = styled(motion.div)<{ $isActive: boolean; $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  ${props => props.$isActive && `
    background: rgba(255, 255, 255, 0.1);
    border-right: 3px solid white;
  `}
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  ${props => props.$collapsed && `
    justify-content: center;
    padding: 1rem 0.75rem;
  `}
`;

const MenuIcon = styled.div<{ $isActive: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all 0.2s ease;
  color: ${props => props.$isActive ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  
  img {
    width: 100%;
    height: 100%;
    filter: ${props => props.$isActive ? 'brightness(0) invert(1)' : 'brightness(0) invert(0.7)'};
  }
`;

const MenuText = styled.span<{ $collapsed: boolean }>`
  color: white;
  font-weight: 500;
  opacity: ${props => props.$collapsed ? 0 : 1};
  transition: opacity 0.3s ease;
  white-space: nowrap;
`;

const MainContent = styled.main<{ $isRTL: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${props => props.$isRTL && `
    order: 1;
  `}
`;

const Header = styled.header`
  background: white;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const Content = styled.div`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  /* Hide horizontal scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.$color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${props => props.$color};
  
  img {
    width: 24px;
    height: 24px;
    filter: brightness(0) invert(1); /* Always white */
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const StatChange = styled.div<{ $positive: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
`;

const SectionTitle = styled.h2`
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const ContentCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ContentHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  overflow-x: hidden;
`;

const ContentTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

const ContentMeta = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const ContentBody = styled.div`
  padding: 1.5rem;
`;

const ContentPreview = styled.div`
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const ContentActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled(motion.button)<{ $variant: 'primary' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.$variant === 'primary' ? `
    background: #dc2626;
    color: white;
    
    &:hover {
      background: #b91c1c;
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
  
  &:hover {
      background: #e5e7eb;
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyDescription = styled.p`
  color: #6b7280;
  margin: 0;
`;

const LogoutSection = styled.div`
  margin-top: auto;
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const LogoutButton = styled(motion.button)<{ $collapsed: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  ${props => props.$collapsed && `
    justify-content: center;
    padding: 1rem 0.75rem;
  `}
`;

const LogoutIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    filter: brightness(0) invert(0.7);
    transition: filter 0.2s ease;
  }
`;

const LogoutText = styled.span<{ $collapsed: boolean }>`
  opacity: ${props => props.$collapsed ? 0 : 1};
  transition: opacity 0.3s ease;
  white-space: nowrap;
`;

const ModernAdminDashboard: React.FC<ModernAdminDashboardProps> = ({ t, isRTL, onLogout }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [content, setContent] = useState<{ [key: string]: ContentItem[] }>({});
  const [loading, setLoading] = useState(false);
  const isCollapsed = true; // Always collapsed, no need for state
  const menuItems = getMenuItems(t);
  const stats = getStats(t);

  useEffect(() => {
    const loadContent = async () => {
    try {
      setLoading(true);
        const sections = ['home', 'about', 'services', 'products', 'markets', 'partners', 'contact'];
        const contentData: { [key: string]: ContentItem[] } = {};

        for (const section of sections) {
          try {
            const data = await FirebaseService.getAll(section);
            contentData[section] = data || [];
          } catch (error) {
            console.error(`Error loading ${section}:`, error);
            contentData[section] = [];
          }
        }
        setContent(contentData);
      } catch (error) {
        console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };
    loadContent();
  }, []);

  const renderContent = () => {
    if (activeSection === 'products') {
      return <AdminProductManager t={t} isRTL={isRTL} />;
    }
    if (activeSection === 'overview') {
        return (
          <>
            <StatsGrid>
              {stats.map((stat, index) => (
                <StatCard
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                >
                <StatHeader>
                  <StatTitle>{stat.title}</StatTitle>
                    <StatIcon $color={stat.color}>
                      <img src={stat.icon} alt={stat.title} />
                    </StatIcon>
                  </StatHeader>
                <StatValue>{stat.value}</StatValue>
                <StatChange $positive={stat.positive}>{stat.change}</StatChange>
                </StatCard>
              ))}
            </StatsGrid>

          <SectionTitle>Recent Activity</SectionTitle>
            <ContentGrid>
            {Object.entries(content).slice(0, 4).map(([section, items]) => (
              <ContentCard
                key={section}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <ContentHeader>
                  <ContentTitle>{section.charAt(0).toUpperCase() + section.slice(1)}</ContentTitle>
                  <ContentMeta>{items.length} items</ContentMeta>
                </ContentHeader>
                <ContentBody>
                  {items.length > 0 ? (
                    <>
                      <ContentPreview>
                        {items[0].title || items[0].description || 'No description available'}
                      </ContentPreview>
                      <ContentActions>
                        <ActionButton $variant="primary" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          View All
                        </ActionButton>
                        <ActionButton $variant="secondary" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          Edit
                        </ActionButton>
                      </ContentActions>
                    </>
                  ) : (
                    <EmptyState>
                      <EmptyIcon>📝</EmptyIcon>
                      <EmptyTitle>No content</EmptyTitle>
                      <EmptyDescription>No items found in this section</EmptyDescription>
                    </EmptyState>
                  )}
                </ContentBody>
              </ContentCard>
            ))}
            </ContentGrid>
          </>
        );
    }

    const sectionContent = content[activeSection] || [];
    
        return (
      <>
        <SectionTitle>{activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}</SectionTitle>
        <ContentGrid>
          {sectionContent.length > 0 ? (
            sectionContent.map((item, index) => (
              <ContentCard
                key={item.id || index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <ContentHeader>
                  <ContentTitle>{item.title || 'Untitled'}</ContentTitle>
                  <ContentMeta>
                    {item.updatedAt ? new Date(item.updatedAt.seconds * 1000).toLocaleDateString() : t.accessibility.noDate}
                  </ContentMeta>
                </ContentHeader>
                <ContentBody>
                  <ContentPreview>
                    {item.description || item.content || 'No description available'}
                  </ContentPreview>
                  <ContentActions>
                    <ActionButton $variant="primary" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      Edit
                    </ActionButton>
                    <ActionButton $variant="secondary" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      Delete
                    </ActionButton>
                  </ContentActions>
                </ContentBody>
              </ContentCard>
            ))
          ) : (
            <EmptyState>
              <EmptyIcon></EmptyIcon>
              <EmptyTitle>No content</EmptyTitle>
              <EmptyDescription>No items found in this section</EmptyDescription>
            </EmptyState>
          )}
        </ContentGrid>
      </>
    );
  };

  return (
    <DashboardContainer $isRTL={isRTL}>
      <Sidebar $collapsed={isCollapsed} $isRTL={isRTL}>
        <SidebarHeader>
          <Logo>
            <img src="/images/logo192.png" alt="Logo" />
            {!isCollapsed && <h1>Admin</h1>}
          </Logo>
        </SidebarHeader>

        <MenuSection>
          <MenuSectionTitle $collapsed={isCollapsed}>Main</MenuSectionTitle>
            {menuItems.filter(item => item.section === 'main').map((item) => (
              <MenuItem
                key={item.id}
              $isActive={activeSection === item.id}
              $collapsed={isCollapsed}
                onClick={() => setActiveSection(item.id)}
              whileHover={{ x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
              <MenuIcon $isActive={activeSection === item.id}>
                <img src={item.icon} alt={item.title} />
              </MenuIcon>
              {!isCollapsed && <MenuText $collapsed={isCollapsed}>{item.title}</MenuText>}
              </MenuItem>
            ))}
          </MenuSection>

          <LogoutSection>
            <LogoutButton
              $collapsed={isCollapsed}
              onClick={onLogout}
              whileHover={{ x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
              <LogoutIcon>
                <img src="/images/admin/logout.svg" alt="Logout" />
              </LogoutIcon>
              {!isCollapsed && <LogoutText $collapsed={isCollapsed}>Logout</LogoutText>}
            </LogoutButton>
          </LogoutSection>
      </Sidebar>

      <MainContent $isRTL={isRTL}>
        <Header>
          <HeaderTitle>
            {activeSection === 'overview' ? t.admin.dashboardOverview : activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}
          </HeaderTitle>
        </Header>

        <Content>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                  <p>Loading...</p>
                </div>
              ) : (
                renderContent()
              )}
            </motion.div>
          </AnimatePresence>
        </Content>
      </MainContent>
    </DashboardContainer>
  );
};

export default ModernAdminDashboard;
